'use client'
import { useMemoizedContentGetter } from 'utils/contentful'
import Section from 'components/Section'
import { PRESS_ROUTE } from 'constants/navigation'
import { AchieveTheme, Grid, Paper } from '@achieve/ascend'
import { Typography } from 'components/Contentful'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { BLOCKS } from '@contentful/rich-text-types'
import { TitleDivider } from 'components/TitleDivider'
import { Carousel } from 'components/Carousels'
import { formatPublishDate } from 'utils/conversions'
import { ExternalLink } from 'react-feather'
import { CONTENT_TYPES } from 'services/contentful/constants'

function Card({ item: { title, publishDate, slug, externalLink, storyType } }) {
  // This is a new implementation from Flat Carousel
  // we are using anything smaller than lg as our mobile for this one
  // for now though, we will make a medium screen resolution later
  const formattedDate = formatPublishDate(publishDate)
  const linkText = storyType == 'Press Release' ? 'Read the press release' : 'Read the article'
  const CardContent = (
    <Grid
      container
      direction={{ xs: 'column', lg: 'row' }}
      justifyContent={{ xs: 'flex-start', lg: 'flex-start' }}
      alignItems={{ xs: 'stretch', lg: 'center' }}
      spacing={{ xs: 4 }}
      className={'lg:bg-white'}
    >
      <Grid container item>
        <Grid size={{ xs: 12 }}>
          <AchieveLink
            href={externalLink ? `${externalLink}` : `${PRESS_ROUTE}/${slug}`}
            className={'no-underline hover:decoration-[#1d252f] [&_p]:m-0'}
            data-testid="featured-press-carousel-title-link"
          >
            <Typography
              content={title}
              variant="headingMd"
              className={
                'text-[#1d252f] overflow-hidden text-ellipsis max-h-[3em] leading-[1.5em] mb-4 lg:max-h-[4.5em] [display:-webkit-box] [line-clamp:2] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] lg:[-webkit-line-clamp:3]'
              }
              fontWeight="medium"
            />
          </AchieveLink>
        </Grid>
        {publishDate && (
          <Grid size={{ xs: 12 }}>
            <Typography
              content={formattedDate}
              fontWeight="regular"
              className={'text-[#1d252f] mx-0 my-3 lg:mt-3 lg:mb-8 lg:mx-0'}
              variant="bodySm"
              data-testid="featured-press-carousel-publish-date"
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }}>
          <AchieveLink
            href={externalLink ? `${externalLink}` : `${PRESS_ROUTE}/${slug}`}
            className={'no-underline hover:decoration-[#1d252f] [&_p]:m-0'}
            data-testid="featured-press-carousel-bottom-link"
          >
            <span className={'inline-flex'} data-testid="featured-press-carousel-bottom-link-text">
              <Typography
                content={linkText}
                className="bodySm lg:bodyLg font-light! lg:font-bold!"
                styleOverride={{
                  [BLOCKS.PARAGRAPH]: {
                    marginTop: '1em',
                  },
                }}
              />
              {storyType == 'In The News' && <ExternalLink className={'ml-1'} />}
            </span>
          </AchieveLink>
        </Grid>
      </Grid>
    </Grid>
  )

  return (
    <Paper
      elevation={0}
      className={
        'p-0 bg-transparent lg:bg-white lg:border lg:p-10 lg:rounded-2xl lg:border-solid lg:border-[#eff2f8]'
      }
    >
      <>{CardContent}</>
    </Paper>
  )
}

const FeaturedPressCarousel = ({ content }) => {
  const sectionIdPrefix = `press-carousel-more-resources-carousel`
  const { featuredPressCarouselTitle, articleType } = useMemoizedContentGetter(content, [
    'featuredPressCarouselTitle',
    'articleType',
  ])
  const featuredArticles = !articleType?.type
    ? content?.fields?.relatedContentCollection?.items
    : content?.fields?.relatedContentCollection?.items?.filter((e) => {
        if (
          ![
            CONTENT_TYPES.ARTICLE.toLowerCase(),
            CONTENT_TYPES.CORPORATE_BLOG.toLowerCase(),
            CONTENT_TYPES.CONSUMER_BLOG.toLowerCase(),
          ].includes(e?.__typename?.toLowerCase())
        ) {
          return false
        }
        if (articleType?.filter) {
          return (
            e?.__typename?.toLowerCase() == articleType?.type?.toLowerCase() &&
            e?.primaryCategory?.toLowerCase() == articleType?.filter?.toLowerCase()
          )
        }
        return e?.__typename?.toLowerCase() == articleType?.type?.toLowerCase()
      })
  return (
    <>
      {featuredArticles && featuredArticles.length > 0 && (
        <Section
          backgroundColor={AchieveTheme?.ascend?.colors?.blue?.[50]}
          className={'[&_section]:bg-blue-50'}
          maxWidth="true"
          data-narrow="false"
          childContainerClassName={'lg:px-0'}
          data-testid="featured-press-carousel-section"
        >
          <Section
            className={
              'bg-white py-16 lg:pr-0 lg:pt-16 lg:pb-[88px] [&_img]:relative lg:[&_img]:relative'
            }
            maxWidth="true"
            data-narrow="false"
            childContainerClassName={'lg:px-0'}
          >
            <TitleDivider
              className={'mb-12 lg:mb-16'}
              title={featuredPressCarouselTitle}
              data-testid={`featured-press-carousel-title`}
              classNameTitleText="displayMd lg:displayXl font-medium!"
              classNameSubtitleText="bodySm lg:displaySm font-light!"
            />
            <Carousel
              carouselItems={featuredArticles}
              itemComponent={Card}
              variant={articleType?.type ? 'preview' : 'preview-small'}
              idPrefix={sectionIdPrefix}
            />
          </Section>
        </Section>
      )}
    </>
  )
}
export { FeaturedPressCarousel }
export default FeaturedPressCarousel
